import {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight, ImageIcon,
  Target,
  CheckCircle2,
  Circle,
  Square,
  Save,
  Trash2,
  Pencil,
  Move,
  X
} from "lucide-react";
import { message, Spin } from "antd";
import axios from "../../utils/axios";

const AnnotationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { images, projectName, projectType, annotationGroup, version } =
    location.state || {};

  const [loading, setLoading] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [annotations, setAnnotations] = useState({});
  const [labelColors, setLabelColors] = useState({});
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentAnnotation, setCurrentAnnotation] = useState(null);
  const [selectedTool, setSelectedTool] = useState(null);
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [pendingAnnotation, setPendingAnnotation] = useState(null);
  const [polygonPoints, setPolygonPoints] = useState([]);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [resizeStartPos, setResizeStartPos] = useState(null);
  const [resizeStartRect, setResizeStartRect] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPos, setDragStartPos] = useState(null);
  const [dragStartAnnotation, setDragStartAnnotation] = useState(null);
  const [wasDragged, setWasDragged] = useState(false);

  // NEW: segment dragging for polyline
  const [draggingSegment, setDraggingSegment] = useState(false);
  const [draggedSegmentIndex, setDraggedSegmentIndex] = useState(null);
  const [segmentDragStartPos, setSegmentDragStartPos] = useState(null);

  // NEW: inserting point on segment
  const [insertingPoint, setInsertingPoint] = useState(false);
  const [newPointIndex, setNewPointIndex] = useState(null);
  const [newPointStartPos, setNewPointStartPos] = useState(null);
  const [scale, setScale] = useState(1);
  const [repeatUsed, setRepeatUsed] = useState({});

  const isDragMode = selectedTool === "drag";

  const user = JSON.parse(localStorage.getItem("user"));
  const organizationId = localStorage.getItem("organizationId");
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  const colorPalette = [
    { bg: "rgba(139, 92, 246, 0.2)", stroke: "#8B5CF6" },
    { bg: "rgba(59, 130, 246, 0.2)", stroke: "#3B82F6" },
    { bg: "rgba(34, 197, 94, 0.2)", stroke: "#22C55E" },
    { bg: "rgba(239, 68, 68, 0.2)", stroke: "#EF4444" },
    { bg: "rgba(249, 115, 22, 0.2)", stroke: "#F97316" },
    { bg: "rgba(236, 72, 153, 0.2)", stroke: "#EC4899" },
    { bg: "rgba(168, 85, 247, 0.2)", stroke: "#A855F7" },
    { bg: "rgba(14, 165, 233, 0.2)", stroke: "#0EA5E9" },
  ];

  const getColorForLabel = (label) => {
    if (!label) return colorPalette[0];
    if (!labelColors[label]) {
      const colorIndex = Object.keys(labelColors).length % colorPalette.length;
      setLabelColors((prev) => ({
        ...prev,
        [label]: colorPalette[colorIndex],
      }));
      return colorPalette[colorIndex];
    }
    return labelColors[label];
  };

  useEffect(() => {
    if (images) {
      const initialAnnotations = {};
      images.forEach((_, index) => {
        initialAnnotations[index] = [];
      });
      setAnnotations(initialAnnotations);
    }
  }, [images]);

  const getCurrentAnnotations = () => annotations[currentImageIndex] || [];

 const getMousePosition = useCallback((e) => {
  const canvas = canvasRef.current;
  if (!canvas) return { x: 0, y: 0 };

  const rect = canvas.getBoundingClientRect();

  return {
    x: (e.clientX - rect.left) / scale,
    y: (e.clientY - rect.top) / scale,
  };
}, [scale]);




  useEffect(() => {
  const container = containerRef.current;
  if (!container) return;

  const onWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.1 : -0.1;
    setScale((s) => Math.min(Math.max(s + delta, 0.2), 5));
  };

  container.addEventListener("wheel", onWheel, { passive: false });

  return () => container.removeEventListener("wheel", onWheel);
}, []);

  const getResizeCursor = (handle) => {
    const cursors = {
      "top-left": "nwse-resize",
      top: "ns-resize",
      "top-right": "nesw-resize",
      right: "ew-resize",
      "bottom-right": "nwse-resize",
      bottom: "ns-resize",
      "bottom-left": "nesw-resize",
      left: "ew-resize",
    };
    return cursors[handle] || "default";
  };

  const getAnnotationBounds = (annotation) => {
    // if (annotation.type === "rectangle" && annotation.points?.length === 4) {
    //   ctx.beginPath();
    //   ctx.moveTo(annotation.points[0].x, annotation.points[0].y);

    //   for (let i = 1; i < 4; i++) {
    //     ctx.lineTo(annotation.points[i].x, annotation.points[i].y);
    //   }

    //   ctx.closePath();
    //   ctx.stroke();
    // }
    if (annotation.type === "rectangle") {
      return {
        x: annotation.x,
        y: annotation.y,
        width: annotation.width,
        height: annotation.height,
      };
    }
    if (annotation.type === "polyline" && annotation.points?.length) {
      const xs = annotation.points.map((p) => p.x);
      const ys = annotation.points.map((p) => p.y);
      const x = Math.min(...xs);
      const y = Math.min(...ys);
      return {
        x,
        y,
        width: Math.max(...xs) - x,
        height: Math.max(...ys) - y,
      };
    }
    return { x: 0, y: 0, width: 0, height: 0 };
  };

  const getResizeHandles = (annotation) => {
    if (annotation.type !== "rectangle") return [];
    const size = 8;
    const { x, y, width: w, height: h } = annotation;
    const positions = [
      ["top-left", x - size / 2, y - size / 2],
      ["top", x + w / 2 - size / 2, y - size / 2],
      ["top-right", x + w - size / 2, y - size / 2],
      ["right", x + w - size / 2, y + h / 2 - size / 2],
      ["bottom-right", x + w - size / 2, y + h - size / 2],
      ["bottom", x + w / 2 - size / 2, y + h - size / 2],
      ["bottom-left", x - size / 2, y + h - size / 2],
      ["left", x - size / 2, y + h / 2 - size / 2],
    ];
    return positions.map(([type, hx, hy]) => ({
      type,
      x: hx,
      y: hy,
      width: size,
      height: size,
    }));
  };

  const isPointInHandle = (point, handle) => {
    return (
      point.x >= handle.x &&
      point.x <= handle.x + handle.width &&
      point.y >= handle.y &&
      point.y <= handle.y + handle.height
    );
  };

  const isPointInAnnotation = (point, annotation) => {
    switch (annotation?.type) {
      case "rectangle":
        return (
          point.x >= annotation.x &&
          point.x <= annotation.x + annotation.width &&
          point.y >= annotation.y &&
          point.y <= annotation.y + annotation.height
        );
      case "polyline":
        if (!annotation.points || annotation.points.length < 2) return false;
        const padding = 8;
        for (let i = 0; i < annotation.points.length; i++) {
          const p1 = annotation.points[i];
          const p2 = annotation.points[(i + 1) % annotation.points.length];
          const dist = distanceToSegment(point, p1, p2);
          if (dist < padding) return true;
        }
        return false;
      default:
        return false;
    }
  };

  const distanceToSegment = (p, v, w) => {
    const l2 = distanceSquared(v, w);
    if (l2 === 0) return distanceToPoint(p, v);
    let t =
      ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;
    t = Math.max(0, Math.min(1, t));
    return distanceToPoint(p, {
      x: v.x + t * (w.x - v.x),
      y: v.y + t * (w.y - v.y),
    });
  };
  const distanceSquared = (v, w) =>
    Math.pow(v.x - w.x, 2) + Math.pow(v.y - w.y, 2);
  const distanceToPoint = (p1, p2) => Math.hypot(p1.x - p2.x, p1.y - p2.y);

  const drawAnnotations = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const currentAnnotations = getCurrentAnnotations();

    currentAnnotations.forEach((annotation, index) => {
      const isSelected = selectedAnnotation === index;
      const colors = getColorForLabel(annotation.text);
      ctx.strokeStyle = isSelected ? "#ef4444" : colors.stroke;
      ctx.fillStyle = isSelected ? "rgba(239, 68, 68, 0.2)" : colors.bg;
      ctx.lineWidth = isSelected ? 3 : 2;
      ctx.setLineDash(isSelected ? [5, 5] : []);

      // RECTANGLE
      if (annotation.type === "rectangle") {

        // 🔵 CASE 1: rectangle with points (from DB)
        if (annotation.points?.length === 4) {
          ctx.beginPath();
          ctx.moveTo(annotation.points[0].x, annotation.points[0].y);

          for (let i = 1; i < 4; i++) {
            ctx.lineTo(annotation.points[i].x, annotation.points[i].y);
          }

          ctx.closePath();
          ctx.stroke();
        }

        // 🟢 CASE 2: rectangle with x,y,width,height (while drawing / local)
        else {
          ctx.strokeRect(
            annotation.x,
            annotation.y,
            annotation.width,
            annotation.height
          );
        }
      }

      // POLYLINE
      if (annotation.type === "polyline" && annotation.points?.length > 1) {
        ctx.beginPath();
        ctx.moveTo(annotation.points[0].x, annotation.points[0].y);
        for (let i = 1; i < annotation.points.length; i++) {
          ctx.lineTo(annotation.points[i].x, annotation.points[i].y);
        }
        ctx.closePath();
        ctx.stroke();

        annotation.points.forEach((p, i) => {
          ctx.fillStyle = isSelected ? "#ef4444" : colors.stroke;
          ctx.beginPath();
          ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
          ctx.fill();

          // Highlight dragged segment
          if (
            isSelected &&
            draggingSegment &&
            (i === draggedSegmentIndex ||
              i === (draggedSegmentIndex + 1) % annotation.points.length)
          ) {
            ctx.strokeStyle = "#f59e0b";
            ctx.lineWidth = 4;
            const next = annotation.points[(i + 1) % annotation.points.length];
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(next.x, next.y);
            ctx.stroke();
          }

          // Highlight newly inserted point being dragged
          if (
            isSelected &&
            insertingPoint &&
            i === newPointIndex
          ) {
            ctx.fillStyle = "#10b981";
            ctx.beginPath();
            ctx.arc(p.x, p.y, 6, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = "#fff";
            ctx.lineWidth = 2;
            ctx.stroke();
          }
        });
      }

      // RESIZE HANDLES – only for selected rectangle
      if (selectedTool === "edit" && isSelected && annotation.type === "rectangle") {
        const handles = getResizeHandles(annotation);
        handles.forEach((h) => {
          ctx.fillStyle = "#ffffff";
          ctx.strokeStyle = "#3b82f6";
          ctx.lineWidth = 2;
          ctx.fillRect(h.x, h.y, h.width, h.height);
          ctx.strokeRect(h.x, h.y, h.width, h.height);
        });
      }

      // LABEL
      const label = annotation.text || `${annotation.type} ${index + 1}`;
      const txt = ctx.measureText(label);
      const pad = 6;
      const w = txt.width + pad * 2;
      const h = 20;
      const lx =
        annotation.x || (annotation.points ? annotation.points[0].x : 0);
      const ly =
        annotation.y || (annotation.points ? annotation.points[0].y : 0);
      ctx.fillStyle = isSelected ? "#ef4444" : colors.stroke;
      ctx.fillRect(lx, ly - h, w, h);
      ctx.fillStyle = "white";
      ctx.font = "12px Inter, system-ui, sans-serif";
      ctx.textBaseline = "middle";
      ctx.fillText(label, lx + pad, ly - h / 2);
    });

    // LIVE RECTANGLE
    if (currentAnnotation && isDrawing && selectedTool === "rectangle") {
      ctx.strokeStyle = "#10b981";
      ctx.fillStyle = "rgba(16, 185, 129, 0.2)";
      ctx.lineWidth = 2;
      ctx.setLineDash([3, 3]);
      ctx.strokeRect(
        currentAnnotation.x,
        currentAnnotation.y,
        currentAnnotation.width,
        currentAnnotation.height
      );
    }

    // LIVE POLYLINE
    if (selectedTool === "polyline" && polygonPoints.length > 0) {
      ctx.strokeStyle = "#10b981";
      ctx.lineWidth = 2;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(polygonPoints[0].x, polygonPoints[0].y);
      for (let i = 1; i < polygonPoints.length; i++) {
        ctx.lineTo(polygonPoints[i].x, polygonPoints[i].y);
      }
      ctx.stroke();
      polygonPoints.forEach((p) => {
        ctx.fillStyle = "#10b981";
        ctx.beginPath();
        ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
        ctx.fill();
      });
      if (canvas.dataset.mouseX !== undefined) {
        const last = polygonPoints[polygonPoints.length - 1];
        ctx.beginPath();
        ctx.moveTo(last.x, last.y);
        ctx.lineTo(+canvas.dataset.mouseX, +canvas.dataset.mouseY);
        ctx.stroke();
      }
      if (polygonPoints.length > 2) {
        const first = polygonPoints[0];
        const mousePos = {
          x: +canvas.dataset.mouseX,
          y: +canvas.dataset.mouseY,
        };
        if (distanceToPoint(mousePos, first) < 15) {
          ctx.strokeStyle = "#ef4444";
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.arc(first.x, first.y, 8, 0, Math.PI * 2);
          ctx.stroke();
        }
      }
    }
    ctx.setLineDash([]);
  }, [
    getCurrentAnnotations,
    selectedAnnotation,
    currentAnnotation,
    isDrawing,
    selectedTool,
    polygonPoints,
    labelColors,
    draggingSegment,
    draggedSegmentIndex,
    insertingPoint,
    newPointIndex,
  ]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onMouseMove = (e) => {
      const pos = getMousePosition(e);
      canvas.dataset.mouseX = pos.x;
      canvas.dataset.mouseY = pos.y;
      drawAnnotations();
    };
    canvas.addEventListener("mousemove", onMouseMove);
    return () => canvas.removeEventListener("mousemove", onMouseMove);
  }, [drawAnnotations, getMousePosition]);

  const saveAnnotations = async () => {
   const allAnnotations = images.map((imgObj, index) => {
  const canvas = canvasRef.current;

  const dW = Number(canvas.dataset.displayWidth);
  const dH = Number(canvas.dataset.displayHeight);
  const nW = Number(canvas.dataset.naturalWidth);
  const nH = Number(canvas.dataset.naturalHeight);

  const scaleX = nW / dW;
  const scaleY = nH / dH;

  return {
    imageUrl: imgObj.url,
    width: nW,
    height: nH,
    annotations: (annotations[index] || []).map((a) => {
  if (a.type === "rectangle") {
    const x1 = a.x;
    const y1 = a.y;
    const x2 = a.x + a.width;
    const y2 = a.y + a.height;

    return {
      type: "rectangle",

      // 🔥 SAVE AS POINTS LIKE POLYGON
      points: [
        { x: x1 * scaleX, y: y1 * scaleY },
        { x: x2 * scaleX, y: y1 * scaleY },
        { x: x2 * scaleX, y: y2 * scaleY },
        { x: x1 * scaleX, y: y2 * scaleY },
      ],

      class: a.text,
    };
  }

  // polygon same as before
  return {
    type: a.type,
    points: a.points
      ? a.points.map((p) => ({
          x: p.x * scaleX,
          y: p.y * scaleY,
        }))
      : [],
    class: a.text,
  };
}),
    splitType: "",
  };
});


  // 🚨 Final safety check
  if (allAnnotations.some(a => !a.width || !a.height)) {
    message.error("Image dimensions not loaded yet. Please wait and try again.");
    return;
  }
    if (!allAnnotations.some((a) => a.annotations.length > 0)) {
      message.warning("No annotations to save!");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post("/annotation-image", {
        username: user.username,
        projectname: projectName,
        organizationId,
        version,
        annotationGroup,
        allAnnotations,
      });
      if (response.status === 200) {
        message.success("Annotations saved successfully!");
        setTimeout(() => navigate("/sidebar/ViewAnnotation"), 1000);
      } else {
        message.error("Failed to save annotations!");
      }
    } catch (error) {
      message.error("An error occurred while saving annotations.");
    } finally {
      setLoading(false);
    }
  };

  const handleMouseDown = (e) => {
    const pos = getMousePosition(e);
    const currentAnnotations = getCurrentAnnotations();

    // 1. RESIZE (only selected rectangle)
    if (selectedTool === "edit" && selectedAnnotation !== null) {
      const ann = currentAnnotations[selectedAnnotation];
      if (selectedTool === "edit" && ann.type === "rectangle") {
        const handles = getResizeHandles(ann);
        for (let i = 0; i < handles.length; i++) {
          if (isPointInHandle(pos, handles[i])) {
            setIsResizing(true);
            setResizeHandle(handles[i].type);
            setResizeStartPos(pos);
            setResizeStartRect(getAnnotationBounds(ann));
            return;
          }
        }
      }
    }

    // 2. INSERT NEW POINT ON POLYLINE SEGMENT
    if (selectedTool === "edit" && selectedAnnotation !== null) {
      const ann = currentAnnotations[selectedAnnotation];
      if (ann.type === "polyline" && ann.points && ann.points.length > 1) {
        const padding = 12;
        for (let i = 0; i < ann.points.length; i++) {
          const p1 = ann.points[i];
          const p2 = ann.points[(i + 1) % ann.points.length];
          const dist = distanceToSegment(pos, p1, p2);
          if (dist < padding) {
            const distToP1 = distanceToPoint(pos, p1);
            const distToP2 = distanceToPoint(pos, p2);
            if (distToP1 > 15 && distToP2 > 15) {
              const t = Math.max(0, Math.min(1,
                ((pos.x - p1.x) * (p2.x - p1.x) + (pos.y - p1.y) * (p2.y - p1.y)) /
                distanceSquared(p1, p2)
              ));
              const newPoint = {
                x: p1.x + t * (p2.x - p1.x),
                y: p1.y + t * (p2.y - p1.y),
              };

              const updated = [...currentAnnotations];
              const points = [...ann.points];
              points.splice(i + 1, 0, newPoint);
              updated[selectedAnnotation] = {
                ...ann,
                points,
              };

              const xs = points.map(p => p.x);
              const ys = points.map(p => p.y);
              updated[selectedAnnotation].x = Math.min(...xs);
              updated[selectedAnnotation].y = Math.min(...ys);
              updated[selectedAnnotation].width = Math.max(...xs) - updated[selectedAnnotation].x;
              updated[selectedAnnotation].height = Math.max(...ys) - updated[selectedAnnotation].y;

              setAnnotations({
                ...annotations,
                [currentImageIndex]: updated,
              });

              setInsertingPoint(true);
              setNewPointIndex(i + 1);
              setNewPointStartPos(pos);
              return;
            }
          }
        }
      }
    }

    // 3. POLYLINE SEGMENT DRAG
    if (selectedTool === "edit" && selectedAnnotation !== null) {
      const ann = currentAnnotations[selectedAnnotation];
      if (ann.type === "polyline" && ann.points) {
        const padding = 8;
        for (let i = 0; i < ann.points.length; i++) {
          const p1 = ann.points[i];
          const p2 = ann.points[(i + 1) % ann.points.length];
          const dist = distanceToSegment(pos, p1, p2);
          if (dist < padding) {
            setDraggingSegment(true);
            setDraggedSegmentIndex(i);
            setSegmentDragStartPos(pos);
            return;
          }
        }
      }
    }

    // 4. DRAG MODE
    if (isDragMode) {
      for (let i = currentAnnotations.length - 1; i >= 0; i--) {
        const ann = currentAnnotations[i];
        if (isPointInAnnotation(pos, ann)) {
          setSelectedAnnotation(i);
          setIsDragging(true);
          setDragStartPos(pos);
          setDragStartAnnotation({
            x: ann.x,
            y: ann.y,
            points: ann.points ? [...ann.points] : null,
          });
          setWasDragged(false); // Reset drag flag
          return;
        }
      }
      return;
    }

    // 5. NORMAL FULL DRAG
    if (selectedAnnotation !== null && !isResizing && !draggingSegment && !insertingPoint) {
      const ann = currentAnnotations[selectedAnnotation];
      if (isPointInAnnotation(pos, ann)) {
        setIsDragging(true);
        setDragStartPos(pos);
        setDragStartAnnotation({
          x: ann.x,
          y: ann.y,
          points: ann.points ? [...ann.points] : null,
        });
        setWasDragged(false); // Reset
        return;
      }
    }

    // 6. DRAWING
    if (isDragMode) return;

    if (selectedTool === "rectangle") {
      setIsDrawing(true);
      setCurrentAnnotation({
        x: pos.x,
        y: pos.y,
        width: 0,
        height: 0,
        type: "rectangle",
      });
      setSelectedAnnotation(null);
      return;
    }

    if (selectedTool === "polyline") {
      if (polygonPoints.length > 0) {
        const first = polygonPoints[0];
        if (distanceToPoint(pos, first) < 15 && polygonPoints.length > 2) {
          finalizePolyline();
          return;
        }
      }
      setPolygonPoints([...polygonPoints, pos]);
      setSelectedAnnotation(null);
      return;
    }

    // 7. SELECTION
    for (let i = currentAnnotations.length - 1; i >= 0; i--) {
      const ann = currentAnnotations[i];
      if (isPointInAnnotation(pos, ann)) {
        setSelectedAnnotation(i);
        return;
      }
    }
    setSelectedAnnotation(null);
  };

  const finalizePolyline = () => {
    if (polygonPoints.length < 3) return;
    const xs = polygonPoints.map((p) => p.x);
    const ys = polygonPoints.map((p) => p.y);
    const xMin = Math.min(...xs);
    const yMin = Math.min(...ys);
    const width = Math.max(...xs) - xMin;
    const height = Math.max(...ys) - yMin;
    const annotation = {
      type: "polyline",
      points: polygonPoints.map((p) => ({ x: p.x, y: p.y })),
      id: Date.now(),
      text: "",
      timestamp: new Date().toISOString(),
      x: xMin,
      y: yMin,
      width,
      height,
    };
    const newAnnotations = [...getCurrentAnnotations(), annotation];
    setAnnotations({
      ...annotations,
      [currentImageIndex]: newAnnotations,
    });
    setPendingAnnotation({ ...annotation, index: newAnnotations.length - 1 });
    setShowLabelModal(true);
    setPolygonPoints([]);
  };

  const handleMouseMove = (e) => {
    const pos = getMousePosition(e);

    // INSERTED POINT DRAG
    if (insertingPoint && newPointStartPos && selectedAnnotation !== null) {
      const dx = pos.x - newPointStartPos.x;
      const dy = pos.y - newPointStartPos.y;
      const updated = [...getCurrentAnnotations()];
      const ann = updated[selectedAnnotation];
      if (ann.points && ann.points[newPointIndex]) {
        ann.points[newPointIndex] = {
          x: ann.points[newPointIndex].x + dx,
          y: ann.points[newPointIndex].y + dy,
        };
        const xs = ann.points.map(p => p.x);
        const ys = ann.points.map(p => p.y);
        ann.x = Math.min(...xs);
        ann.y = Math.min(...ys);
        ann.width = Math.max(...xs) - ann.x;
        ann.height = Math.max(...ys) - ann.y;
      }
      setNewPointStartPos(pos);
      setAnnotations({
        ...annotations,
        [currentImageIndex]: updated,
      });
      return;
    }

    // SEGMENT DRAG
    if (draggingSegment && segmentDragStartPos && selectedAnnotation !== null) {
      const dx = pos.x - segmentDragStartPos.x;
      const dy = pos.y - segmentDragStartPos.y;
      const updated = [...getCurrentAnnotations()];
      const ann = updated[selectedAnnotation];
      if (ann.points && ann.points[draggedSegmentIndex]) {
        ann.points[draggedSegmentIndex] = {
          x: ann.points[draggedSegmentIndex].x + dx,
          y: ann.points[draggedSegmentIndex].y + dy,
        };
        ann.points[(draggedSegmentIndex + 1) % ann.points.length] = {
          x: ann.points[(draggedSegmentIndex + 1) % ann.points.length].x + dx,
          y: ann.points[(draggedSegmentIndex + 1) % ann.points.length].y + dy,
        };
        const xs = ann.points.map((p) => p.x);
        const ys = ann.points.map((p) => p.y);
        ann.x = Math.min(...xs);
        ann.y = Math.min(...ys);
        ann.width = Math.max(...xs) - ann.x;
        ann.height = Math.max(...ys) - ann.y;
      }
      setSegmentDragStartPos(pos);
      setAnnotations({
        ...annotations,
        [currentImageIndex]: updated,
      });
      return;
    }

    // FULL DRAG
    if (selectedTool === "drag" && isDragging && dragStartPos) {
      const dx = pos.x - dragStartPos.x;
      const dy = pos.y - dragStartPos.y;
      const updated = [...getCurrentAnnotations()];
      const ann = updated[selectedAnnotation];
      ann.x = dragStartAnnotation.x + dx;
      ann.y = dragStartAnnotation.y + dy;
      if (ann.points && dragStartAnnotation.points) {
        ann.points = dragStartAnnotation.points.map((p) => ({
          x: p.x + dx,
          y: p.y + dy,
        }));
      }
      setWasDragged(true); // Mark that drag occurred
      setAnnotations({
        ...annotations,
        [currentImageIndex]: updated,
      });
      return;
    }

    // RESIZE
    if (isResizing && resizeStartPos && resizeStartRect && selectedAnnotation !== null) {
      const dx = pos.x - resizeStartPos.x;
      const dy = pos.y - resizeStartPos.y;
      let newX = resizeStartRect.x;
      let newY = resizeStartRect.y;
      let newW = resizeStartRect.width;
      let newH = resizeStartRect.height;
      switch (resizeHandle) {
        case "top-left":
          newX += dx;
          newY += dy;
          newW -= dx;
          newH -= dy;
          break;
        case "top":
          newY += dy;
          newH -= dy;
          break;
        case "top-right":
          newY += dy;
          newW += dx;
          newH -= dy;
          break;
        case "right":
          newW += dx;
          break;
        case "bottom-right":
          newW += dx;
          newH += dy;
          break;
        case "bottom":
          newH += dy;
          break;
        case "bottom-left":
          newX += dx;
          newW -= dx;
          newH += dy;
          break;
        case "left":
          newX += dx;
          newW -= dx;
          break;
      }
      if (newW < 10) newW = 10;
      if (newH < 10) newH = 10;
      const updated = [...getCurrentAnnotations()];
      const ann = updated[selectedAnnotation];
      ann.x = newX;
      ann.y = newY;
      ann.width = newW;
      ann.height = newH;
      setAnnotations({
        ...annotations,
        [currentImageIndex]: updated,
      });
      return;
    }

    // LIVE RECTANGLE DRAW
    if (isDrawing && selectedTool === "rectangle" && currentAnnotation) {
      setCurrentAnnotation((prev) => ({
        ...prev,
        width: pos.x - prev.x,
        height: pos.y - prev.y,
      }));
    }
  };

  const handleMouseUp = (e) => {
    if (insertingPoint) {
      setInsertingPoint(false);
      setNewPointIndex(null);
      setNewPointStartPos(null);
      return;
    }
    if (draggingSegment) {
      setDraggingSegment(false);
      setDraggedSegmentIndex(null);
      setSegmentDragStartPos(null);
      return;
    }
    if (isResizing) {
      setIsResizing(false);
      setResizeHandle(null);
      setResizeStartPos(null);
      setResizeStartRect(null);
      return;
    }
    if (isDrawing && selectedTool === "rectangle") {
      if (!currentAnnotation) return;
      const minSize = 10;
      if (
        Math.abs(currentAnnotation.width) < minSize ||
        Math.abs(currentAnnotation.height) < minSize
      ) {
        setIsDrawing(false);
        setCurrentAnnotation(null);
        return;
      }
      const x1 =
        currentAnnotation.width < 0
          ? currentAnnotation.x + currentAnnotation.width
          : currentAnnotation.x;
      const y1 =
        currentAnnotation.height < 0
          ? currentAnnotation.y + currentAnnotation.height
          : currentAnnotation.y;
      const w = Math.abs(currentAnnotation.width);
      const h = Math.abs(currentAnnotation.height);
      const normalized = {
        x: x1,
        y: y1,
        width: w,
        height: h,
        type: "rectangle",
        id: Date.now(),
        text: "",
        timestamp: new Date().toISOString(),
      };
      const newAnnotations = [...getCurrentAnnotations(), normalized];
      setAnnotations({
        ...annotations,
        [currentImageIndex]: newAnnotations,
      });
      setPendingAnnotation({
        ...normalized,
        index: newAnnotations.length - 1,
      });
      setShowLabelModal(true);
      setIsDrawing(false);
      setCurrentAnnotation(null);
    }

    // DRAG FINISHED — OPEN LABEL MODAL IF DRAGGED
    if (isDragging && wasDragged && selectedAnnotation !== null) {
      const ann = getCurrentAnnotations()[selectedAnnotation];
      setEditingText(ann.text || "");
      setPendingAnnotation({ ...ann, index: selectedAnnotation });
      setShowLabelModal(true);
    }

    setIsDragging(false);
    setDragStartPos(null);
    setDragStartAnnotation(null);
    setWasDragged(false);
  };

  // DOUBLE-CLICK TO CLOSE MODAL
  const handleCanvasDoubleClick = () => {
    if (showLabelModal) {
      setShowLabelModal(false);
      setPendingAnnotation(null);
      setEditingText("");
    }
  };

  const handleCanvasClick = (e) => {
    if (
      isDrawing ||
      isResizing ||
      isDragging ||
      draggingSegment ||
      insertingPoint ||
      isDragMode
    )
      return;
    const pos = getMousePosition(e);
    const currentAnnotations = getCurrentAnnotations();
    for (let i = currentAnnotations.length - 1; i >= 0; i--) {
      const ann = currentAnnotations[i];
      if (isPointInAnnotation(pos, ann)) {
        setSelectedAnnotation(i);
        return;
      }
    }
    setSelectedAnnotation(null);
  };

  const deleteSelectedAnnotation = () => {
    if (selectedAnnotation === null) return;
    const current = getCurrentAnnotations();
    setAnnotations({
      ...annotations,
      [currentImageIndex]: current.filter((_, i) => i !== selectedAnnotation),
    });
    setSelectedAnnotation(null);
  };

  const updateAnnotationText = (newText) => {
    if (pendingAnnotation === null) return;

    const updated = [...getCurrentAnnotations()];

    const previousLabel = [...updated]
      .reverse()
      .find((a) => a.text && a.text.trim() !== "")
      ?.text;

    if (!newText.trim() && !previousLabel) {
      message.warning("Please enter a label before saving.");
      return;
    }

    const labelToUse = newText.trim() || previousLabel;

    updated[pendingAnnotation.index] = {
      ...updated[pendingAnnotation.index],
      text: labelToUse,
    };

    setAnnotations({
      ...annotations,
      [currentImageIndex]: updated,
    });

    setShowLabelModal(false);
    setPendingAnnotation(null);
    setEditingText("");
  };

  // const handleImageLoad = () => {
  //   const canvas = canvasRef.current;
  //   const img = imageRef.current;
  //   if (!canvas || !img) return;
  //   canvas.width = img.naturalWidth;
  //   canvas.height = img.naturalHeight;
  //   drawAnnotations();
  // };

  const handleImageLoad = () => {
  const img = imageRef.current;
  const canvas = canvasRef.current;
  if (!img || !canvas) return;

  const rect = img.getBoundingClientRect();

  canvas.width = rect.width;
  canvas.height = rect.height;

  canvas.style.width = `${rect.width}px`;
  canvas.style.height = `${rect.height}px`;

  // 🔥 store sizes for conversion
  canvas.dataset.displayWidth = rect.width;
  canvas.dataset.displayHeight = rect.height;
  canvas.dataset.naturalWidth = img.naturalWidth;
  canvas.dataset.naturalHeight = img.naturalHeight;

  drawAnnotations();
};


  useEffect(() => {
    drawAnnotations();
  }, [drawAnnotations]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Delete" && selectedAnnotation !== null) {
        deleteSelectedAnnotation();
      }
      if (e.key === "Escape") {
        setSelectedAnnotation(null);
        setShowLabelModal(false);
        setPendingAnnotation(null);
        setPolygonPoints([]);
        setIsResizing(false);
        setIsDragging(false);
        setDraggingSegment(false);
        setInsertingPoint(false);
      }
      if (e.key.toLowerCase() === "r" && currentImageIndex > 0) {

        // 🚫 Prevent repeating again on same image
        if (repeatUsed[currentImageIndex]) {
          message.warning("Annotations already copied for this image.");
          return;
        }

        const prevIdx = currentImageIndex - 1;
        const prev = annotations[prevIdx] || [];

        if (!prev.length) {
          message.info("No annotations to copy from previous image.");
          return;
        }

        const copied = prev.map((a) => ({
          ...a,
          id: Date.now() + Math.random(),
          timestamp: new Date().toISOString(),
        }));
        setAnnotations((p) => ({
          ...p,
          [currentImageIndex]: [...(p[currentImageIndex] || []), ...copied],
        }));

        // ✅ Mark repeat used
        setRepeatUsed((prev) => ({
          ...prev,
          [currentImageIndex]: true
        }));

        message.success(
          `${copied.length} annotation${copied.length > 1 ? "s" : ""} copied!`
        );
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [currentImageIndex, selectedAnnotation, annotations, pendingAnnotation]);

  const getAllLabels = useCallback(() => {
    const all = [];
    Object.values(annotations).forEach((arr) => {
      arr.forEach((a) => {
        if (a.text && !all.includes(a.text)) {
          all.push(a.text);
        }
      });
    });
    return all;
  }, [annotations]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onMove = (e) => {
      if (isDrawing || isResizing || isDragging || draggingSegment || insertingPoint) return;
      const pos = getMousePosition(e);
      let cursor = "default";

      if (selectedTool === "edit" && selectedAnnotation !== null) {
        const ann = getCurrentAnnotations()[selectedAnnotation];
        if (selectedTool === "edit" && ann.type === "rectangle") {
          const handles = getResizeHandles(ann);
          for (const h of handles) {
            if (isPointInHandle(pos, h)) {
              cursor = getResizeCursor(h.type);
              break;
            }
          }
          if (cursor === "default" && isPointInAnnotation(pos, ann)) {
            cursor = "grab";
          }
        } else if (selectedTool === "edit" && ann.type === "polyline") {
          const padding = 12;
          for (let i = 0; i < ann.points.length; i++) {
            const p1 = ann.points[i];
            const p2 = ann.points[(i + 1) % ann.points.length];
            const dist = distanceToSegment(pos, p1, p2);
            const distToP1 = distanceToPoint(pos, p1);
            const distToP2 = distanceToPoint(pos, p2);
            if (dist < padding && distToP1 > 15 && distToP2 > 15) {
              cursor = "crosshair";
              break;
            } else if (dist < padding) {
              cursor = "grab";
              break;
            }
          }
        }
      } else if (isDragMode) {
        for (const ann of getCurrentAnnotations()) {
          if (isPointInAnnotation(pos, ann)) {
            cursor = "grab";
            break;
          }
        }
      } else if (selectedTool === "rectangle") {
        cursor = "crosshair";
      }

      canvas.style.cursor = cursor;
    };
    canvas.addEventListener("mousemove", onMove);
    return () => canvas.removeEventListener("mousemove", onMove);
  }, [
    selectedAnnotation,
    getCurrentAnnotations,
    isDrawing,
    isResizing,
    isDragging,
    draggingSegment,
    insertingPoint,
    getMousePosition,
    isDragMode,
    selectedTool,
  ]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.style.cursor =
      isDragging || draggingSegment || insertingPoint ? "grabbing" : canvas.style.cursor;
  }, [isDragging, draggingSegment, insertingPoint]);


  if (!images || images.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center p-8">
          <ImageIcon className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-600 mb-2">
            No Images Selected
          </h2>
          <p
            onClick={() => navigate("/sidebar/UploadData")}
            className="text-blue-600 hover:underline cursor-pointer"
          >
            Please select images in upload data to start annotating
          </p>
        </div>
      </div>
    );
  }

  const currentImage = images[currentImageIndex];
  const currentAnnotations = getCurrentAnnotations();

  const goToPrevious = () => {
    setCurrentImageIndex((p) =>
      p > 0 ? p - 1 : images.length - 1
    );
    setSelectedAnnotation(null);
    setCurrentAnnotation(null);
    setPolygonPoints([]);
    setIsResizing(false);
    setIsDragging(false);
    setDraggingSegment(false);
    setInsertingPoint(false);
  };
  const goToNext = () => {
    setCurrentImageIndex((p) =>
      p < images.length - 1 ? p + 1 : 0
    );
    setSelectedAnnotation(null);
    setCurrentAnnotation(null);
    setPolygonPoints([]);
    setIsResizing(false);
    setIsDragging(false);
    setDraggingSegment(false);
    setInsertingPoint(false);
  };

  const totalAnnotations = Object.values(annotations).reduce(
    (s, a) => s + a.length,
    0
  );
  const annotatedImages = Object.values(annotations).filter(
    (a) => a.length > 0
  ).length;

  const tools = [
    { id: "rectangle", name: "Rectangle", icon: Square, tip: "Draw rectangle" },
    {
      id: "polyline",
      name: "Polyline",
      icon: Pencil,
      tip: "Draw polygon (click to close)",
    },
    { id: "drag", name: "Drag", icon: Move, tip: "Drag to move annotations" },
    { id: "edit", name: "Edit", icon: Pencil, tip: "Edit selected annotation" }

  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-slate-200/60 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              
              
              <div>
                <h1 className="text-xl font-bold text-slate-800 flex items-center">
                  <Target className="w-5 h-5 mr-2 text-blue-600" />
                  {projectName || "Image Annotation"}
                </h1>
                <p className="text-sm text-slate-500 mt-0.5">
                  Press <kbd className="px-1.5 py-0.5 bg-slate-200 rounded text-xs">R</kbd> to repeat **all** previous annotations
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden sm:flex items-center space-x-2 text-sm">
                <div className="flex items-center space-x-1 text-slate-600">
                  <Circle className="w-3 h-3" />
                  <span>{currentImageIndex + 1} of {images.length}</span>
                </div>
                <div className="w-px h-4 bg-slate-300"></div>
                <div className="flex items-center space-x-1 text-emerald-600">
                  <CheckCircle2 className="w-3 h-3" />
                  <span>{annotatedImages} annotated</span>
                </div>
              </div>
              <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full transition-all duration-300"
                  style={{
                    width: `${
                      ((currentImageIndex + 1) / images.length) * 100
                    }%`,
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 z-50">
          <Spin size="large" tip="Saving annotations..." />
        </div>
      )}

      {/* Main Layout */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
          {/* Left Column */}
          <div className="xl:col-span-3">
            <div className="flex gap-5">
              {/* Toolbar */}
              <div className="relative group">
                <div className="bg-white rounded-2xl shadow-xl border border-slate-200/60 p-4 flex flex-col items-center space-y-3 w-20 h-fit sticky top-24">
  
                  <div className="text-xs font-semibold text-slate-500 mb-2">
                    Tools
                  </div>

                  {/* TOOL BUTTONS */}
                  {tools.map((tool) => {
                    const Icon = tool.icon;
                    const active = selectedTool === tool.id;
                    return (
                      <div key={tool.id} className="relative" title={tool.tip}>
                        <button
                          onClick={() => {
                            if (selectedTool === "polyline" && polygonPoints.length >= 3) {
                              finalizePolyline();
                            }
                            setSelectedTool(tool.id);
                            setPolygonPoints([]);
                            setIsResizing(false);
                            setIsDragging(false);
                            setDraggingSegment(false);
                            setInsertingPoint(false);
                          }}
                          className={`relative p-3 rounded-xl transition-all duration-300 flex items-center justify-center group/tool ${
                            active
                              ? "bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg ring-4 ring-blue-500/30 scale-110"
                              : "text-slate-600 hover:bg-slate-100 hover:shadow-md hover:scale-105"
                          }`}
                        >
                          <Icon className="w-6 h-6" />
                          {active && (
                            <div className="absolute -inset-1 bg-blue-500/20 rounded-xl blur-xl -z-10"></div>
                          )}
                        </button>
                      </div>
                    );
                  })}

                  {/* Divider */}
                  <div className="h-px w-12 bg-slate-200 my-2"></div>

                  {/* 🔥 ZOOM CONTROLS INSIDE TOOLBAR */}
                  <div className="flex flex-col space-y-2 w-full items-center mt-2">

                    <button
                      onClick={() => setScale((s) => Math.min(s + 0.1, 4))}
                      className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700 font-bold"
                      title="Zoom In"
                    >
                      +
                    </button>

                    <button
                      onClick={() => setScale((s) => Math.max(s - 0.1, 0.3))}
                      className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700 font-bold"
                      title="Zoom Out"
                    >
                      –
                    </button>

                    <button
                      onClick={() => setScale(1)}
                      className=" p-1 rounded-lg bg-white border border-slate-300 text-xs text-slate-600 hover:bg-slate-100 font-medium"
                      title="Reset Zoom"
                    >
                      Reset
                    </button>

                  </div>

                  {/* Instructions */}
                  <div className="h-px w-12 bg-slate-200 mt-3"></div>
                  <div className="text-[10px] text-slate-400 text-center leading-tight">
                    {selectedTool === "drag"
                      ? "Drag to move"
                      : selectedTool === "rectangle"
                      ? "Draw rectangle"
                      : selectedTool === "polyline"
                      ? "Click to draw"
                      : "Select a tool"}
                  </div>
                </div>

              </div>

              {/* Canvas */}
              <div className="flex-1 space-y-6">
                <div className="bg-white rounded-xl shadow-lg border border-slate-200/60 p-4">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={goToPrevious}
                      disabled={images.length <= 1}
                      className="flex items-center px-4 py-2.5 text-slate-600 hover:text-slate-800 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all duration-200 border border-slate-200 hover:border-slate-300 hover:shadow-sm"
                    >
                      <ChevronLeft className="w-4 h-4 mr-2" />
                      <span className="font-medium">Previous</span>
                    </button>
                    <div className="text-center">
                      <div className="text-sm font-semibold text-slate-700">
                        Image {currentImageIndex + 1} of {images.length}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {currentAnnotations.length} annotation
                        {currentAnnotations.length !== 1 ? "s" : ""}
                      </div>
                    </div>
                    <button
                      onClick={goToNext}
                      disabled={images.length <= 1}
                      className="flex items-center px-4 py-2.5 text-slate-600 hover:text-slate-800 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all duration-200 border border-slate-200 hover:border-slate-300 hover:shadow-sm"
                    >
                      <span className="font-medium">Next</span>
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </button>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 overflow-hidden border border-slate-200/60">
                  <div className="p-8 bg-gradient-to-br from-slate-50/50 to-white">
                    <div
                      ref={containerRef}
                      className="relative rounded-xl overflow-hidden shadow-lg border border-slate-200/60 max-w-full inline-block"
                      style={{
                        transform: `scale(${scale})`,
                        transformOrigin: "center center",
                      }}
                    >
                      {/* IMAGE */}
                      <img
                        ref={imageRef}
                        src={currentImage.url}
                        alt=""
                        className="max-w-full h-auto block"
                        onLoad={handleImageLoad}
                      />

                      {/* CANVAS */}
                      <canvas
                        ref={canvasRef}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          cursor:
                            isDragging || draggingSegment || insertingPoint
                              ? "grabbing"
                              : isResizing
                              ? getResizeCursor(resizeHandle)
                              : isDragMode
                              ? "grab"
                              : selectedTool === "rectangle"
                              ? "crosshair"
                              : "default",
                        }}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onClick={handleCanvasClick}
                        onDoubleClick={handleCanvasDoubleClick}
                      />
                    </div>

                    {/* Empty helper text */}
                    {currentAnnotations.length === 0 && (
                      <div className="mt-4 text-center">
                        <p className="text-sm text-slate-500 bg-slate-50 rounded-lg px-4 py-3 border border-slate-200">
                          {selectedTool
                            ? `Using ${selectedTool} tool — ${
                                tools.find((t) => t.id === selectedTool)?.tip
                              }`
                            : "Select a tool to start annotating"}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="xl:col-span-1">
            <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-200/60 sticky top-24">
              <div className="p-6 border-b border-slate-200/60">
                <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
                  <Target className="w-5 h-5 mr-2 text-blue-600" />
                  Image {currentImageIndex + 1} Annotations
                </h2>
                {currentAnnotations.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Target className="w-6 h-6 text-slate-400" />
                    </div>
                    <p className="text-slate-500 text-sm">
                      No annotations yet
                    </p>
                    <p className="text-slate-400 text-xs mt-1">
                      Click on an annotation to edit
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100">
                    {currentAnnotations.map((ann, idx) => {
                      const colors = getColorForLabel(ann.text);
                      return (
                        <div
                          key={ann.id}
                          onClick={() => {
                            setSelectedAnnotation(idx);
                            setEditingText(ann.text);
                            setPendingAnnotation({ ...ann, index: idx });
                            setShowLabelModal(true);
                          }}
                          className={`group p-4 rounded-xl border transition-all duration-200 cursor-pointer hover:shadow-sm ${
                            selectedAnnotation === idx ? "ring-2 ring-red-500" : ""
                          }`}
                          style={{
                            borderColor: colors.stroke,
                            backgroundColor: colors.bg,
                          }}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div
                              className="font-semibold text-sm flex items-center"
                              style={{ color: colors.stroke }}
                            >
                              <div
                                className={`w-3 h-3 rounded-full mr-2 ${
                                  selectedAnnotation === idx
                                    ? "bg-red-500"
                                    : ""
                                }`}
                                style={{
                                  backgroundColor:
                                    selectedAnnotation === idx
                                      ? "#ef4444"
                                      : colors.stroke,
                                }}
                              ></div>
                              {ann.text || "Untitled"}
                            </div>
                            <div className="text-xs text-slate-400">
                              #{idx + 1}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
                            <div className="bg-white rounded-md px-2 py-1 border border-slate-100">
                              <span className="font-medium">X:</span>{" "}
                              {Math.round(
                                ann.x ||
                                  (ann.points && ann.points[0]?.x) ||
                                  0
                              )}
                            </div>
                            <div className="bg-white rounded-md px-2 py-1 border border-slate-100">
                              <span className="font-medium">Y:</span>{" "}
                              {Math.round(
                                ann.y ||
                                  (ann.points && ann.points[0]?.y) ||
                                  0
                              )}
                            </div>
                            {["rectangle", "polyline"].includes(
                              ann.type
                            ) && (
                              <>
                                <div className="bg-white rounded-md px-2 py-1 border border-slate-100">
                                  <span className="font-medium">W:</span>{" "}
                                  {Math.round(ann.width)}
                                </div>
                                <div className="bg-white rounded-md px-2 py-1 border border-slate-100">
                                  <span className="font-medium">H:</span>{" "}
                                  {Math.round(ann.height)}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="p-6">
                <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center">
                  <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" />
                  Project Summary
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-blue-50/50 rounded-lg border border-blue-100">
                    <span className="text-sm font-medium text-slate-600">
                      Total Images
                    </span>
                    <span className="text-sm font-bold text-blue-600">
                      {images.length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-emerald-50 to-emerald-50/50 rounded-lg border border-emerald-100">
                    <span className="text-sm font-medium text-slate-600">
                      Annotated
                    </span>
                    <span className="text-sm font-bold text-emerald-600">
                      {annotatedImages}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-purple-50/50 rounded-lg border border-purple-100">
                    <span className="text-sm font-medium text-slate-600">
                      Total Annotations
                    </span>
                    <span className="text-sm font-bold text-purple-600">
                      {totalAnnotations}
                    </span>
                  </div>
                </div>

                <div className="mt-4 p-4 bg-gradient-to-r from-slate-50 to-slate-50/50 rounded-lg border border-slate-100">
                  <div className="flex items-center justify-between text-xs text-slate-600 mb-2">
                    <span>Progress</span>
                    <span>
                      {Math.round(
                        (annotatedImages / images.length) * 100
                      )}
                      %
                    </span>
                  </div>
                  <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full transition-all duration-500"
                      style={{
                        width: `${
                          (annotatedImages / images.length) * 100
                        }%`,
                      }}
                    ></div>
                  </div>
                </div>

                <div className="mt-4 flex justify-center">
                  <button
                    onClick={saveAnnotations}
                    disabled={loading}
                    className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                      loading
                        ? "bg-blue-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700 text-white"
                    }`}
                  >
                    {loading ? (
                      <Spin size="small" className="mr-2" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    {loading ? "Saving..." : "Save Annotations"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Label Modal */}
      {showLabelModal && pendingAnnotation !== null && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
        <div className="bg-gradient-to-b from-zinc-900 to-black border border-zinc-800 rounded-2xl p-6 max-w-md w-full shadow-2xl shadow-black/50 animate-in zoom-in-95 duration-200">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-xl font-semibold text-white mb-1">
                {pendingAnnotation.id ? "Edit Label" : "Enter Label"}
              </h3>
              <p className="text-sm text-zinc-400">
                for {pendingAnnotation.type}
              </p>
            </div>
            <button
              onClick={() => {
                setShowLabelModal(false);
                setPendingAnnotation(null);
                setEditingText('');
              }}
              className="text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg p-2 transition-all duration-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Input Field */}
          <div className="mb-4">
            <input
              type="text"
              value={editingText}
              onChange={(e) => setEditingText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") updateAnnotationText(editingText);
              }}
              className="w-full px-4 py-3 bg-zinc-800/50 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200"
              placeholder="Enter label..."
              autoFocus
            />
          </div>

          {/* Suggestions Dropdown */}
          {getAllLabels().length > 0 && (
            <div className="mb-4">
              <p className="text-xs text-zinc-400 mb-2 font-medium uppercase tracking-wider">
                Recent Labels
              </p>
              <div className="max-h-40 overflow-y-auto rounded-xl bg-zinc-800/30 border border-zinc-800">
                {getAllLabels().map((label, index) => (
                  <div
                    key={label}
                    onClick={() => setEditingText(label)}
                    className={`px-4 py-3 text-zinc-300 hover:bg-zinc-700/50 hover:text-white cursor-pointer text-sm transition-all duration-150 ${
                      index !== getAllLabels().length - 1 ? 'border-b border-zinc-800' : ''
                    }`}
                  >
                    {label}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between items-center pt-4 border-t border-zinc-800">
            <div>
              {pendingAnnotation.id && (
                <button
                  onClick={() => {
                    deleteSelectedAnnotation();
                    setShowLabelModal(false);
                    setPendingAnnotation(null);
                    setEditingText('');
                  }}
                  className="flex items-center px-4 py-2.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-xl transition-all duration-200 font-medium"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </button>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowLabelModal(false);
                  setPendingAnnotation(null);
                  setEditingText('');
                }}
                className="px-5 py-2.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-xl transition-all duration-200 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  updateAnnotationText(editingText);
                }}
                className="flex items-center px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl hover:from-blue-500 hover:to-blue-400 transition-all duration-200 shadow-lg shadow-blue-500/20 font-medium"
              >
                <Save className="w-4 h-4 mr-2" />
                Save
              </button>
            </div>
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

export default AnnotationPage;